import { useEffect, useRef, useState } from 'react';
import './App.css';

const navigation = [
  { href: '#how-it-works', label: 'How it works' },
  { href: '#proof', label: 'Proof' },
  { href: '#faq', label: 'FAQ' },
];

const heroSignals = [
  'Be the first to try Pace',
  'Early access first',
  'No spam. Ever.',
];

const painSignals = [
  {
    icon: 'brain',
    title: "You're not lazy.",
    text: 'Your mind is overloaded.',
  },
  {
    icon: 'list',
    title: 'You have a plan.',
    text: "It's buried in tabs, lists, and half-started tasks.",
  },
  {
    icon: 'ring',
    title: 'Every option feels heavy.',
    text: 'So you stall, switch, or scroll.',
  },
];

const mechanismSteps = [
  {
    number: '1',
    title: 'Write report',
    highlight: 'Type the task.',
    text: 'Say it in plain language. Pace gets it.',
    sample: 'I need to write my report.',
    icon: 'keyboard',
  },
  {
    number: '2',
    title: 'Too big',
    highlight: 'Pace gives you a start.',
    text: 'Not a plan. Not a list. Just the next right move.',
    sample: "Open the doc. Write the title. That's it.",
    icon: 'spark',
  },
  {
    number: '3',
    title: 'Begin',
    highlight: 'Do the first step.',
    text: 'Momentum grows when the first move is obvious.',
    sample: 'Opening the doc...',
    icon: 'play',
  },
];

const featureRail = [
  {
    icon: 'brain',
    title: 'AI-personalized',
    text: 'Built around your goals, energy, and real life.',
  },
  {
    icon: 'target',
    title: 'Actionable',
    text: 'One clear first step you can take right now.',
  },
  {
    icon: 'bars',
    title: 'Progress that sticks',
    text: 'Small wins compound into lasting change.',
  },
];

const proofScenarios = [
  {
    title: 'Walk for 10 minutes outside',
    detail: 'Actionable. Small. Doable in the next 2 hours.',
    worked: 'Quick outdoor resets made starting easier.',
    reminder: 'Keep the first step frictionless on low-energy days.',
    focus: 'Protect the next easy win before the day fills up.',
  },
  {
    title: 'Open the deck and write one headline',
    detail: 'Low pressure. Clear target. Enough to start momentum.',
    worked: 'Tiny drafting steps made the project feel lighter.',
    reminder: 'Do not expand the task before you begin it.',
    focus: 'Use one sharp first move to break planning loops.',
  },
  {
    title: 'Reply to the first client email only',
    detail: 'One message. One send. No inbox cleanup spiral.',
    worked: 'Single-response blocks reduced mental switching.',
    reminder: 'Scope creep is what makes simple tasks feel heavy.',
    focus: 'Keep batching small and obvious instead of ambitious.',
  },
];

const defaultProofState = {
  stepIndex: 0,
  streak: 12,
  completed: 4,
  progress: 18,
  cooldown: 0,
  completedThisRound: false,
  insightSaved: false,
};

const proofCooldownSeconds = 10;

const audienceCards = [
  {
    title: 'Freelancers',
    copy: 'No boss. No pressure. Infinite deferral.',
  },
  {
    title: 'ADHD brains',
    copy: 'Not broken. Just overloaded at the start.',
  },
  {
    title: 'Self-improvement people',
    copy: 'Too much planning. Not enough movement.',
  },
];

const faqs = [
  {
    question: 'What exactly does Pace do?',
    answer:
      'You type the thing you cannot begin. Pace turns it into one clear first step so you can move without negotiating with yourself.',
  },
  {
    question: 'Is this another giant task manager?',
    answer:
      "No. Pace is focused on activation. It is built for the moment where you already know the task exists but still cannot start it.",
  },
  {
    question: 'When is it launching?',
    answer:
      'Soon. The waitlist gets launch updates, early access, and first dibs when the app opens.',
  },
];

function LogoMark() {
  return (
    <div className="brand">
      <img
        className="brand__logo"
        src={`${process.env.PUBLIC_URL}/pace-logo.png`}
        alt="Pace logo"
      />
      <span className="brand__wordmark">Pace</span>
    </div>
  );
}

function Icon({ name }) {
  switch (name) {
    case 'spark':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 2.8 14.6 9l6.2 2.6-6.2 2.6L12 20.4l-2.6-6.2-6.2-2.6L9.4 9 12 2.8Z" />
        </svg>
      );
    case 'keyboard':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="3" y="6" width="18" height="12" rx="3" />
          <path d="M7 10h1M10 10h1M13 10h1M16 10h1M7 13h7M16 13h1" />
        </svg>
      );
    case 'play':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <path d="m10 8 6 4-6 4V8Z" />
        </svg>
      );
    case 'brain':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M9.5 5.5a3.2 3.2 0 1 0-5.5 2.2A4 4 0 0 0 6 15.2V17a2.5 2.5 0 0 0 5 0v-1.3" />
          <path d="M14.5 5.5A3.2 3.2 0 1 1 20 7.7 4 4 0 0 1 18 15.2V17a2.5 2.5 0 0 1-5 0v-1.3" />
          <path d="M12 4v14M8 10.2c1 0 1.6.4 2.2 1.1M16 10.2c-1 0-1.6.4-2.2 1.1" />
        </svg>
      );
    case 'list':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M8 7h10M8 12h10M8 17h10" />
          <circle cx="5" cy="7" r="1" />
          <circle cx="5" cy="12" r="1" />
          <circle cx="5" cy="17" r="1" />
        </svg>
      );
    case 'ring':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="8" strokeDasharray="2 3" />
        </svg>
      );
    case 'target':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="8" />
          <circle cx="12" cy="12" r="4" />
          <path d="m12 12 6-6" />
        </svg>
      );
    case 'bars':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 18V9M12 18V5M18 18v-7" />
        </svg>
      );
    case 'bolt':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M13 2 5 13h5l-1 9 8-11h-5l1-9Z" />
        </svg>
      );
    case 'arrow':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M5 12h14M13 5l7 7-7 7" />
        </svg>
      );
    default:
      return null;
  }
}

function SectionEyebrow({ children, dark }) {
  return (
    <div className={`section-eyebrow${dark ? ' section-eyebrow--dark' : ''}`}>
      <span className="section-eyebrow__dot" />
      <span>{children}</span>
    </div>
  );
}

function WaitlistForm({
  joinedEmail,
  onJoin,
  dark = false,
  source,
  compact = false,
  microcopy,
}) {
  const [email, setEmail] = useState(joinedEmail || '');
  const [status, setStatus] = useState(joinedEmail ? 'success' : 'idle');
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    if (joinedEmail) {
      setEmail(joinedEmail);
      setStatus('success');
      setFeedback((current) => current || "You're in. We saved your spot.");
    }
  }, [joinedEmail]);

  async function handleSubmit(event) {
    event.preventDefault();
    const trimmedEmail = email.trim().toLowerCase();
    const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isLocalPreview =
      typeof window !== 'undefined' &&
      ['localhost', '127.0.0.1'].includes(window.location.hostname);
    const endpoint = process.env.REACT_APP_WAITLIST_ENDPOINT || (!isLocalPreview ? '/api/waitlist' : '');

    if (!validEmail.test(trimmedEmail) || trimmedEmail.length > 254) {
      setStatus('error');
      setFeedback('Enter a real email address.');
      return;
    }

    setStatus('loading');
    setFeedback('Submitting...');

    try {
      if (endpoint) {
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), 10000);
        let response;

        try {
          response = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: trimmedEmail,
              source,
              wantsTesterAccess: true,
            }),
            signal: controller.signal,
          });
        } finally {
          window.clearTimeout(timeoutId);
        }

        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(payload.error || 'waitlist request failed');
        }

        setFeedback(payload.message || "You're in. We saved your spot.");
      } else if (typeof window !== 'undefined') {
        const stored = JSON.parse(window.localStorage.getItem('pace-waitlist') || '[]');
        const alreadyJoined = stored.includes(trimmedEmail);
        const next = alreadyJoined ? stored : [...stored, trimmedEmail];
        window.localStorage.setItem('pace-waitlist', JSON.stringify(next));

        setFeedback(
          alreadyJoined
            ? "You're already on the preview list."
            : 'Saved locally for preview. Connect Supabase on Vercel to collect it live.',
        );
      }

      setStatus('success');
      onJoin(trimmedEmail);
    } catch (error) {
      setStatus('error');
      setFeedback(
        error?.name === 'AbortError'
          ? 'Signup timed out. Try again in a minute.'
          : error?.message || 'Signup failed. Try again in a minute.',
      );
    }
  }

  return (
    <div className={`waitlist-card${dark ? ' waitlist-card--dark' : ''}${compact ? ' waitlist-card--compact' : ''}`}>
      <form className="waitlist-form" onSubmit={handleSubmit}>
        <label className="sr-only" htmlFor={`waitlist-${source}`}>
          Email address
        </label>
        <div className="waitlist-form__field">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <rect x="3" y="5" width="18" height="14" rx="3" />
            <path d="m5 8 7 5 7-5" />
          </svg>
          <input
            id={`waitlist-${source}`}
            name="email"
            type="email"
            autoComplete="email"
            placeholder="Enter your email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={status === 'loading'}
          />
        </div>
        <button className="button button--primary" type="submit" disabled={status === 'loading'}>
          {status === 'loading' ? 'Joining...' : 'Join the waitlist'}
          <span className="button__icon" aria-hidden="true">
            <Icon name="arrow" />
          </span>
        </button>
      </form>
      <div className={`waitlist-card__meta${status === 'error' ? ' waitlist-card__meta--error' : ''}`}>
        <span>{feedback || microcopy}</span>
      </div>
    </div>
  );
}

function PhoneShell({ dark = false, className = '', children }) {
  return (
    <div className={`phone-shell${dark ? ' phone-shell--dark' : ''} ${className}`.trim()}>
      <div className="phone-shell__frame" />
      <div className="phone-shell__topbar">
        <span>9:41</span>
        <div className="phone-shell__island" />
        <div className="phone-shell__status">
          <span />
          <span />
          <span />
        </div>
      </div>
      <div className="phone-shell__body">{children}</div>
    </div>
  );
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [joinedEmail, setJoinedEmail] = useState('');
  const [openFaq, setOpenFaq] = useState(0);
  const [headerVisible, setHeaderVisible] = useState(true);
  const [headerCompact, setHeaderCompact] = useState(false);
  const [activeMechanism, setActiveMechanism] = useState(1);
  const [proofState, setProofState] = useState(() => ({ ...defaultProofState }));
  const lastScrollYRef = useRef(0);
  const insightResetRef = useRef(null);

  useEffect(() => {
    const reducedMotionQuery =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : null;
    const reduceMotion = reducedMotionQuery ? reducedMotionQuery.matches : false;
    const canObserve = typeof window.IntersectionObserver === 'function';
    const requestFrame =
      typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame.bind(window)
        : (callback) => callback();

    const revealTargets = document.querySelectorAll('[data-reveal]');
    const parallaxTargets = document.querySelectorAll('[data-parallax]');

    if (reduceMotion || !canObserve) {
      revealTargets.forEach((target) => target.classList.add('is-visible'));
      if (!canObserve) {
        return undefined;
      }
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      {
        threshold: 0.18,
      },
    );

    revealTargets.forEach((target) => observer.observe(target));

    let ticking = false;

    const updateParallax = () => {
      const currentScrollY = window.scrollY || window.pageYOffset || 0;
      const scrollDelta = currentScrollY - lastScrollYRef.current;
      const nextHeaderVisible = currentScrollY < 32 || scrollDelta < -6;
      const nextHeaderCompact = currentScrollY > 24;

      setHeaderVisible((current) => (current === nextHeaderVisible ? current : nextHeaderVisible));
      setHeaderCompact((current) => (current === nextHeaderCompact ? current : nextHeaderCompact));

      parallaxTargets.forEach((target) => {
        const speed = Number(target.dataset.parallax || 0.08);
        const rect = target.getBoundingClientRect();
        const distance = (window.innerHeight * 0.5 - rect.top) * speed;
        target.style.setProperty('--parallax-y', `${distance.toFixed(1)}px`);
      });

      lastScrollYRef.current = currentScrollY;
      ticking = false;
    };

    const handleScroll = () => {
      if (ticking) {
        return;
      }

      ticking = true;
      requestFrame(updateParallax);
    };

    updateParallax();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle('menu-open', menuOpen);
    return () => document.body.classList.remove('menu-open');
  }, [menuOpen]);

  useEffect(() => {
    if (menuOpen) {
      setHeaderVisible(true);
    }
  }, [menuOpen]);

  useEffect(() => {
    if (proofState.cooldown <= 0) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setProofState((current) => {
        if (current.cooldown <= 1) {
          return { ...defaultProofState };
        }

        return {
          ...current,
          cooldown: current.cooldown - 1,
        };
      });
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [proofState.cooldown]);

  useEffect(() => {
    if (!proofState.insightSaved) {
      return undefined;
    }

    insightResetRef.current = window.setTimeout(() => {
      setProofState((current) => ({
        ...current,
        insightSaved: false,
      }));
    }, 4000);

    return () => {
      if (insightResetRef.current) {
        window.clearTimeout(insightResetRef.current);
      }
    };
  }, [proofState.insightSaved]);

  function closeMenu() {
    setMenuOpen(false);
  }

  const activeProofScenario = proofScenarios[proofState.stepIndex];
  const proofProgressWidth = `${Math.min(100, (proofState.completed / 7) * 100)}%`;
  const proofActionLabel =
    proofState.cooldown > 0 ? `Logged - resets in ${proofState.cooldown}s` : 'I did it';

  function handleProofDone() {
    setProofState((current) => {
      if (current.cooldown > 0) {
        return current;
      }

      return {
        ...current,
        completedThisRound: true,
        streak: Math.min(current.streak + 1, 99),
        completed: Math.min(current.completed + 1, 7),
        progress: Math.min(current.progress + 4, 99),
        cooldown: proofCooldownSeconds,
      };
    });
  }

  function handleProofScenarioChange() {
    setProofState((current) => {
      if (current.cooldown > 0) {
        return current;
      }

      return {
        ...current,
        stepIndex: (current.stepIndex + 1) % proofScenarios.length,
      };
    });
  }

  function handleInsightSave() {
    setProofState((current) => {
      if (current.insightSaved) {
        return current;
      }

      return {
        ...current,
        insightSaved: true,
      };
    });
  }

  return (
    <div className="page-shell">
      <header
        className={`site-header${headerCompact ? ' site-header--compact' : ''}${
          !menuOpen && !headerVisible ? ' site-header--hidden' : ''
        }`}
      >
        <div className="container site-header__inner">
          <a className="site-header__brand" href="#top" aria-label="Pace home" onClick={closeMenu}>
            <LogoMark />
          </a>

          <nav className="site-nav" aria-label="Primary navigation">
            {navigation.map((item) => (
              <a key={item.href} href={item.href}>
                {item.label}
              </a>
            ))}
          </nav>

          <div className="site-header__actions">
            <a className="button button--ghost" href="#waitlist">
              Join the waitlist
            </a>
            <button
              className={`menu-toggle${menuOpen ? ' menu-toggle--open' : ''}`}
              type="button"
              aria-label="Toggle navigation"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((current) => !current)}
            >
              <span />
              <span />
            </button>
          </div>
        </div>

        <div className={`mobile-nav${menuOpen ? ' mobile-nav--open' : ''}`}>
          {navigation.map((item) => (
            <a key={item.href} href={item.href} onClick={closeMenu}>
              {item.label}
            </a>
          ))}
          <a className="button button--primary mobile-nav__cta" href="#waitlist" onClick={closeMenu}>
            Join the waitlist
            <span className="button__icon" aria-hidden="true">
              <Icon name="arrow" />
            </span>
          </a>
        </div>
      </header>

      <main>
        <section className="section hero" id="top">
          <div className="hero__glow hero__glow--one" />
          <div className="hero__glow hero__glow--two" />
          <div className="hero__wave" />
          <div className="hero__lines" />

          <div className="container hero__grid">
            <div className="hero__copy" data-reveal>
              <SectionEyebrow>Warm-brutal productivity</SectionEyebrow>
              <h1>
                Stop planning.
                <br />
                Start doing.
              </h1>
              <p className="hero__lede">
                Pace gives you one clear first step when your brain stalls.
              </p>
              <p className="hero__support">
                When you cannot begin, Pace cuts through the noise and tells you exactly what to do next.
              </p>

              <WaitlistForm
                joinedEmail={joinedEmail}
                onJoin={setJoinedEmail}
                source="hero"
                microcopy="Coming soon. Early access first. No spam."
              />

              <div className="hero__signals">
                {heroSignals.map((signal) => (
                  <div className="signal-chip" key={signal}>
                    <span className="signal-chip__check">✓</span>
                    <span>{signal}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="hero__visual" data-reveal>
              <div className="floating-note floating-note--top" data-parallax="0.1">
                <span className="floating-note__icon">
                  <Icon name="spark" />
                </span>
                <p>Cuts decision fatigue</p>
              </div>

              <div className="floating-note floating-note--middle" data-parallax="0.13">
                <span className="floating-note__icon">
                  <Icon name="target" />
                </span>
                <p>Focus on what moves you</p>
              </div>

              <div className="floating-note floating-note--bottom" data-parallax="0.09">
                <span className="floating-note__icon">
                  <Icon name="ring" />
                </span>
                <p>Build momentum one step at a time</p>
              </div>

              <PhoneShell className="hero-phone">
                <div className="phone-app phone-app--light">
                  <div className="phone-app__header">
                    <LogoMark />
                    <button className="icon-button" type="button" aria-label="Open menu">
                      <span />
                      <span />
                      <span />
                    </button>
                  </div>

                  <div className="phone-hero-copy">
                    <h3>
                      Turn vague
                      <br />
                      into <span>clear.</span>
                    </h3>
                    <p>Pace turns overwhelm into one actionable first step.</p>
                  </div>

                  <div className="task-card">
                    <span className="task-card__label">Your task</span>
                    <div className="task-card__content">
                      <p>Write my project report</p>
                      <div className="task-card__scribble" />
                    </div>
                  </div>

                  <div className="phone-arrow">
                    <span>↓</span>
                  </div>

                  <div className="task-card task-card--success">
                    <span className="task-card__label">Your first step</span>
                    <div className="task-card__content">
                      <p>Open the doc. Write the title. That's it.</p>
                      <div className="task-card__check">✓</div>
                    </div>
                  </div>

                  <div className="bottom-tabs">
                    <span className="bottom-tabs__item bottom-tabs__item--active">Today</span>
                    <button className="bottom-tabs__add" type="button" aria-label="Add task">
                      +
                    </button>
                    <span className="bottom-tabs__item">History</span>
                  </div>
                </div>
              </PhoneShell>

              <a className="hero__scroll" href="#how-it-works">
                Scroll to see how it works
                <span>↓</span>
              </a>
            </div>
          </div>
        </section>

        <section className="section pain-mirror" id="about">
          <div className="container">
            <div className="pain-layout">
              <div className="pain-copy panel panel--soft" data-reveal>
                <SectionEyebrow>The problem</SectionEyebrow>
                <h2>
                  You are not lazy.
                  <br />
                  You are <span>stuck at the start.</span>
                </h2>
                <p className="section-copy">
                  The task is there. The list is there. You still do not move.
                </p>
                <p className="section-note">That is not a character flaw. It is a system problem.</p>

                <div className="pain-points">
                  {painSignals.map((signal) => (
                    <div className="pain-point" key={signal.title}>
                      <div className="pain-point__icon">
                        <Icon name={signal.icon} />
                      </div>
                      <div>
                        <h3>{signal.title}</h3>
                        <p>{signal.text}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="quote-card">
                  <span className="quote-card__mark">“</span>
                  <p>
                    The hardest part <span>is starting.</span>
                  </p>
                  <small>Not because you do not care. Because it is too much.</small>
                </div>
              </div>

              <div className="pain-visual panel panel--dark" data-reveal>
                <SectionEyebrow dark>Right now</SectionEyebrow>
                <h2>
                  Overloaded. Scattered.
                  <br />
                  Frozen.
                </h2>

                <div className="overwhelm-stack">
                  <div className="overwhelm-card overwhelm-card--tasks" data-parallax="0.08">
                    <h3>Tasks</h3>
                    <ul>
                      <li>Launch plan</li>
                      <li>Budget review</li>
                      <li>Client follow up</li>
                      <li>Website updates</li>
                    </ul>
                  </div>
                  <div className="overwhelm-card overwhelm-card--brain" data-parallax="0.12">
                    <h3>Brain dump</h3>
                    <ul>
                      <li>Things I should be doing</li>
                      <li>What if this does not work?</li>
                      <li>Where do I even start?</li>
                      <li>Too much on my plate</li>
                    </ul>
                  </div>
                  <div className="overwhelm-card overwhelm-card--today" data-parallax="0.06">
                    <h3>Today</h3>
                    <ul>
                      <li>Workout</li>
                      <li>Work on deck</li>
                      <li>Read</li>
                    </ul>
                  </div>
                  <div className="overwhelm-loader" data-parallax="0.15">
                    <p>Getting started...</p>
                    <div className="loader-spinner" />
                  </div>
                </div>

                <div className="pace-relief">
                  <SectionEyebrow>With Pace</SectionEyebrow>
                  <h3>
                    Clear. Focused.
                    <span> In motion.</span>
                  </h3>
                  <div className="pace-relief__cards">
                    <div>
                      <span>Today</span>
                      <strong>Start the deck</strong>
                      <small>30 min</small>
                    </div>
                    <div>
                      <span>Up next</span>
                      <strong>Client follow up</strong>
                      <small>Only after the first step</small>
                    </div>
                    <div>
                      <span>Progress</span>
                      <strong>1 done</strong>
                      <small>One clear next step. That is all you need.</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="section mechanism" id="how-it-works">
          <div className="container">
            <div className="mechanism__heading" data-reveal>
              <SectionEyebrow>How Pace works</SectionEyebrow>
              <h2>
                Most apps give you a list.
                <br />
                Pace gives you <span>a start.</span>
              </h2>
              <p className="section-copy">
                Progress, not pressure. Pace turns any task into a single obvious first step so you can move without overthinking.
              </p>
              <p className="section-note">That is why it works.</p>
            </div>

            <div className="mechanism__layout">
              <div className="mechanism__cards">
                {mechanismSteps.map((step, index) => (
                  <article
                    key={step.number}
                    className={`mechanism-card panel panel--soft${
                      activeMechanism === index ? ' mechanism-card--active' : ''
                    }`}
                    data-reveal
                    style={{ transitionDelay: `${index * 110}ms` }}
                    tabIndex={0}
                    role="button"
                    aria-pressed={activeMechanism === index}
                    onClick={() => setActiveMechanism(index)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setActiveMechanism(index);
                      }
                    }}
                  >
                    <div className="mechanism-card__number">{step.number}</div>
                    <div className="mechanism-card__icon">
                      <Icon name={step.icon} />
                    </div>
                    <span className="mechanism-card__tag">{step.title}</span>
                    <h3>{step.highlight}</h3>
                    <p>{step.text}</p>
                    <div className="mechanism-card__sample">{step.sample}</div>
                  </article>
                ))}
              </div>

              <div className="mechanism__phone" data-reveal>
                <PhoneShell dark className="mechanism-phone">
                  <div className="phone-app phone-app--dark">
                    <div className="phone-app__header">
                      <h3>Pace</h3>
                      <button className="icon-button icon-button--dark" type="button" aria-label="Open menu">
                        <span />
                        <span />
                        <span />
                      </button>
                    </div>

                    <div className="chat-thread">
                      <div className="chat-bubble chat-bubble--user">
                        <span>You</span>
                        <p>{mechanismSteps[0].sample}</p>
                      </div>
                      <div className="chat-bubble chat-bubble--pace">
                        <span>Pace</span>
                        <p>
                          {activeMechanism >= 1
                            ? mechanismSteps[1].sample
                            : 'Type the task first. Pace works from plain language.'}
                        </p>
                        <small>
                          {activeMechanism >= 2
                            ? 'Good. Keep going with the first small move.'
                            : 'Just the next right move.'}
                        </small>
                      </div>
                      <div className="chat-bubble chat-bubble--user">
                        <span>You</span>
                        <p>
                          {activeMechanism >= 2
                            ? mechanismSteps[2].sample
                            : 'Waiting for the first move...'}
                        </p>
                      </div>
                    </div>

                    <div className="chat-input">
                      <input type="text" value="" placeholder="Type anything..." readOnly />
                      <button type="button" aria-label="Send">
                        ↑
                      </button>
                    </div>
                  </div>
                </PhoneShell>
              </div>
            </div>

            <div className="mechanism-callout panel panel--dark" data-reveal>
              <div className="mechanism-callout__icon">
                <Icon name="bolt" />
              </div>
              <div>
                <strong>Low activation energy. High forward motion.</strong>
                <p>That is the Pace mechanism.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="section proof" id="proof">
          <div className="proof__glow" />
          <div className="proof__wave" />
          <div className="container proof__grid">
            <div className="proof__copy" data-reveal>
              <SectionEyebrow dark>See Pace in action</SectionEyebrow>
              <h2>
                It tells you what to do next.
                <br />
                <span>Not just what is overdue.</span>
              </h2>
              <p className="section-copy section-copy--dark">
                One first step. Every time. Personalized by AI, backed by science, designed for real life.
              </p>
              <div className="proof__line">
                <span className="proof__spark">✦</span>
                <span>Finally move.</span>
              </div>
              <a className="button button--lime" href="#waitlist">
                Join the waitlist
                <span className="button__icon" aria-hidden="true">
                  <Icon name="arrow" />
                </span>
              </a>
            </div>

            <div className="proof__phone-wrap" data-reveal>
              <PhoneShell dark className="proof-phone">
                <div className="phone-app phone-app--dark phone-app--proof">
                  <div className="proof-phone__welcome">Good morning, Alex</div>
                  <div className="proof-step" aria-live="polite">
                    <span>Your first step</span>
                    <strong>{activeProofScenario.title}</strong>
                    <small>{activeProofScenario.detail}</small>
                  </div>
                  <div className="proof-step proof-step--action">
                    <button type="button" onClick={handleProofDone} disabled={proofState.cooldown > 0}>
                      {proofActionLabel}
                    </button>
                    <button
                      className="proof-step__secondary"
                      type="button"
                      onClick={handleProofScenarioChange}
                      disabled={proofState.cooldown > 0}
                    >
                      {proofState.cooldown > 0 ? 'Locked during reset' : 'Try another first step'}
                    </button>
                  </div>
                  <div className="proof-streak" aria-live="polite">
                    <span>Your streak</span>
                    <strong>{proofState.streak}</strong>
                    <small>days in a row</small>
                    <div className="proof-streak__bars">
                      {Array.from({ length: 7 }).map((_, index) => (
                        <i
                          key={`streak-bar-${index}`}
                          className={
                            index === 6 || (proofState.completedThisRound && index === 5)
                              ? 'proof-streak__bars--active'
                              : ''
                          }
                        />
                      ))}
                    </div>
                  </div>
                  <div className="proof-progress" aria-live="polite">
                    <span>This week</span>
                    <strong>{`${proofState.completed} / 7 steps completed`}</strong>
                    <div className="proof-progress__bar">
                      <span style={{ width: proofProgressWidth }} />
                    </div>
                  </div>
                  <div className="phone-footer-nav">
                    <span className="phone-footer-nav__item phone-footer-nav__item--active">Today</span>
                    <span className="phone-footer-nav__item">Progress</span>
                    <span className="phone-footer-nav__item">Coach</span>
                    <span className="phone-footer-nav__item">You</span>
                  </div>
                </div>
              </PhoneShell>
            </div>

            <div className="proof__panel panel panel--glass" data-reveal>
              <div className="proof-panel__header">
                <span className="proof-panel__badge">Weekly AI review</span>
              </div>
              <h3>You kept showing up.</h3>
              <p>You built momentum with consistency. Here is what stood out and how to keep it going.</p>

              <div className="proof-metrics">
                <div className="proof-metric">
                  <strong>{`${proofState.completed} / 7`}</strong>
                  <span>Steps completed</span>
                </div>
                <div className="proof-metric">
                  <strong>{proofState.streak}</strong>
                  <span>Day streak</span>
                </div>
                <div className="proof-metric">
                  <strong>{`+${proofState.progress}%`}</strong>
                  <span>Progress vs last week</span>
                </div>
              </div>

              <div className="proof-panel__stack">
                <div>
                  <span>What worked</span>
                  <p>{activeProofScenario.worked}</p>
                </div>
                <div>
                  <span>Keep in mind</span>
                  <p>{activeProofScenario.reminder}</p>
                </div>
                <div>
                  <span>Focus for next week</span>
                  <p>{activeProofScenario.focus}</p>
                </div>
              </div>

              <button className="proof-panel__button" type="button" onClick={handleInsightSave}>
                {proofState.insightSaved ? 'Saved for next week' : 'Sounds good'}
              </button>
            </div>
          </div>

          <div className="container">
            <div className="feature-rail" data-reveal>
              {featureRail.map((item) => (
                <article className="feature-rail__item" key={item.title}>
                  <div className="feature-rail__icon">
                    <Icon name={item.icon} />
                  </div>
                  <div>
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section audience" id="audience">
          <div className="audience__curve" />
          <div className="container">
            <div className="audience__heading" data-reveal>
              <SectionEyebrow>Why Pace</SectionEyebrow>
              <h2>
                Built for people who know what to do
                <br />
                and still <span>cannot start.</span>
              </h2>
              <p className="section-copy">
                Pace removes friction, clears the noise, and keeps you moving so you actually finish what matters.
              </p>
            </div>

            <div className="audience__cards">
              {audienceCards.map((card, index) => (
                <article
                  className="audience-card panel panel--soft"
                  key={card.title}
                  data-reveal
                  style={{ transitionDelay: `${index * 110}ms` }}
                >
                  <div className="audience-card__icon">
                    <span>{`0${index + 1}`}</span>
                  </div>
                  <h3>{card.title}</h3>
                  <p>{card.copy}</p>
                  <div className="audience-card__mini-ui">
                    {index === 0 && (
                      <>
                        <span>Client deck</span>
                        <strong>Open slides. Write headline.</strong>
                      </>
                    )}
                    {index === 1 && (
                      <>
                        <span>Today</span>
                        <strong>Start with the easiest clean win.</strong>
                      </>
                    )}
                    {index === 2 && (
                      <>
                        <span>Progress</span>
                        <strong>3 / 5 done. Keep momentum.</strong>
                      </>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section cta-repeat" id="waitlist">
          <div className="cta-repeat__glow" />
          <div className="container cta-repeat__content" data-reveal>
            <SectionEyebrow>If starting is the hard part</SectionEyebrow>
            <h2>Join the waitlist.</h2>
            <p className="section-copy">Get launch updates and first access.</p>

            <WaitlistForm
              joinedEmail={joinedEmail}
              onJoin={setJoinedEmail}
              source="repeat"
              microcopy="Coming soon."
              compact
            />

            <div className="faq-block" id="faq">
              <div className="faq-block__header">
                <SectionEyebrow>FAQ</SectionEyebrow>
                <p>Short answers. No fluff.</p>
              </div>

              <div className="faq-list">
                {faqs.map((item, index) => {
                  const isOpen = openFaq === index;

                  return (
                    <article className={`faq-item${isOpen ? ' faq-item--open' : ''}`} key={item.question}>
                      <button type="button" onClick={() => setOpenFaq(index)}>
                        <span>{item.question}</span>
                        <span>{isOpen ? '−' : '+'}</span>
                      </button>
                      <div className="faq-item__body">
                        <p>{item.answer}</p>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section className="section final-close">
          <div className="final-close__halo" />
          <div className="container final-close__content" data-reveal>
            <SectionEyebrow dark>You're almost in</SectionEyebrow>
            <h2>
              Your brutal best friend.
              <br />
              <span>In app form.</span>
            </h2>
            <p className="section-copy section-copy--dark">One first step. Every time.</p>

            <WaitlistForm
              joinedEmail={joinedEmail}
              onJoin={setJoinedEmail}
              source="final"
              dark
              microcopy="Coming soon. Early access first. No spam."
            />
          </div>

          <footer className="site-footer">
            <div className="container site-footer__inner">
              <div className="site-footer__brand">
                <LogoMark />
                <p>Pace - coming soon</p>
              </div>

              <div className="site-footer__links">
                <a href="#how-it-works">How it works</a>
                <a href="#proof">Product proof</a>
                <a href="#faq">FAQ</a>
              </div>

              <a className="button button--outline" href="#top">
                Back to top
              </a>
            </div>
          </footer>
        </section>
      </main>
    </div>
  );
}

export default App;
